import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router';
import { useGetUserAnnotationsQuery, useGetUserExternalDevicesQuery, useGetUsersQuery } from 'features/api/apiSlice';

// material-ui
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { LineChartOutlined, FileTextOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyUserCard from 'components/cards/skeleton/EmptyUserCard';
import { PatientChartTab } from 'pages/patients/tabs/PatientChartTab';
import { SessionSummary } from './tabs/SessionSummary';
import { selectCurrentUser } from 'features/auth/authSlice';
import { useAppSelector } from 'app/hooks';
  
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// ==============================|| CUSTOMER - CARD PREVIEW ||============================== //
  
export default function PatientFileDialog () {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);
  const { userid } = useParams();
  const [tabValue, setTabValue] = useState<number>(0);
  
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: null })

  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetUserExternalDevicesQuery(userid!!)

  const { data: annotations = [] } = useGetUserAnnotationsQuery({
    userId: userid!!,
    from: null,
    to: new Date().toISOString()
  })

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (isLoadingUsers || isLoadingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('loading', 'Loading...')} />
      </Grid>
    )
  } else if (isFetchingUsers || isFetchingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('fetching', 'Fetching...')} />
      </Grid>
    )
  } else {
    const patient = users!!.find(x => x.id == userid)

    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="account profile tab">
            <Tab label={t('session_summary', 'Session summary')} icon={<FileTextOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(0)}/>
            <Tab label={t('charts', 'Charts')} icon={<LineChartOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(1)}/>
          </Tabs>
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <CustomTabPanel value={tabValue} index={0}>
            <SessionSummary patientId={patient!!.id!!} users={users!!} />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            <PatientChartTab
              patientId={patient!!.id!!}
              users={users!!.concat(authUser!!)}
              externalDevices={externalDevices!!}
              annotations={annotations}
            />
          </CustomTabPanel>
          <Box sx={{ mt: 2.5 }}>
            <Outlet />
          </Box>
        </Box>
      </>
    )
  }
}
  